import type { InvoicePreviewQuery, OperatorListQuery } from '@sirius/contracts';
import { queryOptions } from '@tanstack/react-query';

import { getSession } from './endpoints/auth';
import { getBidsSummary, getDealer, getDealerProfile, getDealersSummary, getDealerTimeline, getDestinationUsage, getInlandPriceList, getInvoicePreview, getInvoicesSummary, getPaymentDetails, getRates, getTopUpsSummary, getVehiclesSummary, listAttention, listBids, listDealerLedger, listDealers, listDealerStatusHistory, listDealerTopUps, listInvoices, listOperators, listTopUps, listVehicles } from './endpoints/operator';

const SESSION_KEY = ['session'] as const;
const BIDS_KEY = ['operator', 'bids'] as const;
const DEALERS_KEY = ['operator', 'dealers'] as const;
const VEHICLES_KEY = ['operator', 'vehicles'] as const;
const INVOICES_KEY = ['operator', 'invoices'] as const;
const RATES_KEY = ['operator', 'rates'] as const;
const INLAND_KEY = ['operator', 'inland'] as const;
const ATTENTION_KEY = ['operator', 'attention'] as const;
const BIDS_SUMMARY_KEY = ['operator', 'bids', 'summary'] as const;
const DEALERS_SUMMARY_KEY = ['operator', 'dealers', 'summary'] as const;
const VEHICLES_SUMMARY_KEY = ['operator', 'vehicles', 'summary'] as const;
const INVOICES_SUMMARY_KEY = ['operator', 'invoices', 'summary'] as const;
const PAYMENT_DETAILS_KEY = ['operator', 'payment-details'] as const;
const TOP_UPS_KEY = ['operator', 'top-ups'] as const;
const TOP_UPS_SUMMARY_KEY = ['operator', 'top-ups', 'summary'] as const;

const sessionOptions = queryOptions({
	queryKey: SESSION_KEY,
	queryFn: getSession,
	staleTime: 60_000,
	refetchOnWindowFocus: true,
	retry: false,
});
/**
 * Оператор працює з даними, які змінюють інші (дилери ставлять, завантажують
 * підтвердження). Тому свіжість коротка, а перехід між сторінками завжди
 * перепитує; real-time — Phase 2.
 */
const LIVE = { staleTime: 0, refetchInterval: 30_000, refetchOnWindowFocus: true } as const;
const attentionOptions = queryOptions({ queryKey: ATTENTION_KEY, queryFn: () => listAttention(), ...LIVE });
/** Операторів мало й міняються вони рідко — опитувати їх щопівхвилини нема сенсу. */
const OPERATORS_KEY = ['operator', 'operators'] as const;
const operatorsOptions = queryOptions({ queryKey: OPERATORS_KEY, queryFn: listOperators });
const bidsSummaryOptions = queryOptions({ queryKey: BIDS_SUMMARY_KEY, queryFn: getBidsSummary, ...LIVE });
const dealersSummaryOptions = queryOptions({ queryKey: DEALERS_SUMMARY_KEY, queryFn: getDealersSummary, ...LIVE });
const vehiclesSummaryOptions = queryOptions({ queryKey: VEHICLES_SUMMARY_KEY, queryFn: getVehiclesSummary, ...LIVE });
const invoicesSummaryOptions = queryOptions({ queryKey: INVOICES_SUMMARY_KEY, queryFn: getInvoicesSummary, ...LIVE });
const topUpsSummaryOptions = queryOptions({ queryKey: TOP_UPS_SUMMARY_KEY, queryFn: getTopUpsSummary, ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerOptions = (id: string) =>
	queryOptions({ queryKey: [...DEALERS_KEY, 'detail', id] as const, queryFn: () => getDealer(id), ...LIVE });
/*
  Профіль дилера — під тим самим ключем, що й список дилерів: зарахування,
  списання і зміна доступу вже інвалідують dealersKey, тож сторінка профілю
  оновлюється без жодного нового рядка в мутаціях.
*/
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerProfileOptions = (id: string) =>
	queryOptions({ queryKey: [...DEALERS_KEY, 'profile', id] as const, queryFn: () => getDealerProfile(id), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerTimelineOptions = (id: string) =>
	queryOptions({ queryKey: [...DEALERS_KEY, 'timeline', id] as const, queryFn: () => getDealerTimeline(id), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerLedgerOptions = (id: string, query: OperatorListQuery) =>
	queryOptions({ queryKey: [...DEALERS_KEY, 'ledger', id, query] as const, queryFn: () => listDealerLedger(id, query), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerTopUpsOptions = (id: string, query: OperatorListQuery) =>
	queryOptions({ queryKey: [...DEALERS_KEY, 'top-ups', id, query] as const, queryFn: () => listDealerTopUps(id, query), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerStatusHistoryOptions = (id: string, query: OperatorListQuery) =>
	queryOptions({ queryKey: [...DEALERS_KEY, 'status-history', id, query] as const, queryFn: () => listDealerStatusHistory(id, query), ...LIVE });
// Черга одного дилера — під ключем черги, тож дії з неї інвалідують і цю, і загальну.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealerAttentionOptions = (dealerId: string) =>
	queryOptions({ queryKey: [...ATTENTION_KEY, dealerId] as const, queryFn: () => listAttention(dealerId), ...LIVE });
/**
 * Списки залежать від фільтрів, тож параметри входять у ключ: кожна комбінація
 * має власний чесний запис у кеші, а не переписує чужий.
 *
 * Виняток із правила «явний тип повернення всюди» — той самий, що в apps/web:
 * анотація стерла б брендування queryOptions і зробила б useQuery менш типобезпечним.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const bidsOptions = (query: OperatorListQuery) =>
	queryOptions({ queryKey: [...BIDS_KEY, query] as const, queryFn: () => listBids(query), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const dealersOptions = (query: OperatorListQuery) =>
	queryOptions({ queryKey: [...DEALERS_KEY, query] as const, queryFn: () => listDealers(query), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const vehiclesOptions = (query: OperatorListQuery) =>
	queryOptions({ queryKey: [...VEHICLES_KEY, query] as const, queryFn: () => listVehicles(query), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const invoicesOptions = (query: OperatorListQuery) =>
	queryOptions({ queryKey: [...INVOICES_KEY, query] as const, queryFn: () => listInvoices(query), ...LIVE });
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const topUpsOptions = (query: OperatorListQuery) =>
	queryOptions({ queryKey: [...TOP_UPS_KEY, query] as const, queryFn: () => listTopUps(query), ...LIVE });
// Прев'ю інвойсу для форми Won: суми змінюються разом із полями форми.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const invoicePreviewOptions = (bidId: string, query: InvoicePreviewQuery) =>
	queryOptions({ queryKey: [...BIDS_KEY, bidId, 'invoice-preview', query] as const, queryFn: () => getInvoicePreview(bidId, query) });
// Тарифи міняються раз на місяці й лише звідси, тож опитувати їх щопівхвилини нема сенсу.
/** Прайс вантажівки міняється раз на місяць, а довідник майданчиків — ще рідше. */
const inlandOptions = queryOptions({
	queryKey: INLAND_KEY,
	queryFn: getInlandPriceList,
	staleTime: 5 * 60_000,
});

const ratesOptions = queryOptions({ queryKey: RATES_KEY, queryFn: getRates, staleTime: 5 * 60_000 });
/*
  Що тримає порт — питаємо щоразу, коли оператор відкриває «Remove»: ставки й
  авто міняються без нього. Ключ під тарифами, тож будь-яка зміна тарифів
  скидає і його.
*/
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const destinationUsageOptions = (code: string) =>
	queryOptions({ queryKey: [...RATES_KEY, 'usage', code] as const, queryFn: () => getDestinationUsage(code), staleTime: 0 });
const paymentDetailsOptions = queryOptions({ queryKey: PAYMENT_DETAILS_KEY, queryFn: getPaymentDetails, staleTime: 5 * 60_000 });

export const sessionQueries = {
	all: (): typeof SESSION_KEY => SESSION_KEY,
	current: (): typeof sessionOptions => sessionOptions,
};

export const operatorQueries = {
	attentionKey: (): typeof ATTENTION_KEY => ATTENTION_KEY,
	attention: (): typeof attentionOptions => attentionOptions,
	dealerAttention: dealerAttentionOptions,
	bidsKey: (): typeof BIDS_KEY => BIDS_KEY,
	bids: bidsOptions,
	bidsSummary: (): typeof bidsSummaryOptions => bidsSummaryOptions,
	invoicePreview: invoicePreviewOptions,
	dealersKey: (): typeof DEALERS_KEY => DEALERS_KEY,
	dealers: dealersOptions,
	dealer: dealerOptions,
	dealerProfile: dealerProfileOptions,
	dealerTimeline: dealerTimelineOptions,
	dealerLedger: dealerLedgerOptions,
	dealerTopUps: dealerTopUpsOptions,
	dealerStatusHistory: dealerStatusHistoryOptions,
	dealersSummary: (): typeof dealersSummaryOptions => dealersSummaryOptions,
	vehiclesKey: (): typeof VEHICLES_KEY => VEHICLES_KEY,
	vehicles: vehiclesOptions,
	vehiclesSummary: (): typeof vehiclesSummaryOptions => vehiclesSummaryOptions,
	invoicesKey: (): typeof INVOICES_KEY => INVOICES_KEY,
	invoices: invoicesOptions,
	invoicesSummary: (): typeof invoicesSummaryOptions => invoicesSummaryOptions,
	topUps: topUpsOptions,
	topUpsSummary: (): typeof topUpsSummaryOptions => topUpsSummaryOptions,
	operatorsKey: (): typeof OPERATORS_KEY => OPERATORS_KEY,
	operators: (): typeof operatorsOptions => operatorsOptions,
	ratesKey: (): typeof RATES_KEY => RATES_KEY,
	rates: (): typeof ratesOptions => ratesOptions,
	inland: (): typeof inlandOptions => inlandOptions,
	destinationUsage: destinationUsageOptions,
	paymentDetailsKey: (): typeof PAYMENT_DETAILS_KEY => PAYMENT_DETAILS_KEY,
	paymentDetails: (): typeof paymentDetailsOptions => paymentDetailsOptions,
	/** Усе операторське одним префіксом: після оплати змінюються інвойс, авто, дилер і ставка. */
	allKey: (): readonly ['operator'] => ['operator'] as const,
};
